import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Settings/Settings.module.css';
import { useMsal } from "@azure/msal-react";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Outlet, useLocation } from 'react-router-dom';
import InvoiceNumPopup from '../../../Components/InvoiceNumPopUp/InvoiceNumPopUp';
import { useSelector, useDispatch } from "../../../redux/store";
import { getCurrentInvNum, postInvoiceNum, postInvoiceNumUnpaid, fetchInvoiceExport, fetchKFInvoiceExport, fetchKFPaidExport, setShowSuccess } from "../../../redux/slices/invoice";
import { getConfiguration, updateSTDTemplateResponsibilities, setShowSettingsSuccess, getEmailRouting, updateEmailRouting, sendProjectToVm } from "../../../redux/slices/settings";
import { postResubmitFBP, fetchWIPExport, fetchFullProjectExport, fetchPhasesExport, exportTimesheets } from "../../../redux/slices/project";
import { fetchClientExport } from "../../../redux/slices/client";
import { exportSuppliers } from '../../../redux/slices/suppliers';
import { exportEmployees } from '../../../redux/slices/settings';
import KFInvoiceExportPopup from '../../../Components/KFInvoiceExportPopup/KFInvoiceExportPopup';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ProjectLogbookPopUp from '../../../Components/ProjectLogbookPopUp/ProjectLogbookPopUp';

// Import notistack's enqueueSnackbar
import { useSnackbar } from 'notistack';
import { onboardEmployee, fetchAdminCalendarData, fetchAdminHolidayOverviewRows, fetchAdminRequestedLeaveList } from '../../../redux/slices/adminHr';
import ResponsibilitiesForm from '../../../Sections/settings/responsibilitiesForm';
import EmailRoutingForm from '../../../Sections/settings/EmailRoutingForm';
import ProjectNumPopUp from '../../../Components/ProjectNumPopUp/ProjectNumPopUp';
import OnboardEmployeePopup from '../../../Components/Forms/OnboardEmployeePopup';
import PersonalLinksForm from '../../../Sections/settings/PersonalLinksForm';
import LevelsRatesPopup from '../../../Components/Forms/LevelsRatesPopup';
import JobTitlesPopup from '../../../Components/Forms/JobTitlesPopup';
import ManageMomentUserPopup from '../../../Components/Forms/ManageMomentUserPopup';
import Textfeild from '../../../Components/Textfeild/Textfeild';

function Settings() {
  const dispatch = useDispatch();
  const [reload, setReload] = useState(0);
  const { accounts } = useMsal();
  const [wipLoading, setWipLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [kfInvoiceLoading, setKfInvoiceLoading] = useState(false);
  const [kfPaidLoading, setKfPaidLoading] = useState(false);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [phaseLoading, setPhaseLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [showLogbookPopup, setShowLogbookPopup] = useState(false);

  const account = accounts[0];
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [InvoiceMode, setInvoiceMode] = useState(null);
  const [showFBPResub, setShowFBPResub] = useState(false);
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const [showFormResPopup, setShowFormResPopup] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [showOnboardPopup, setShowOnboardPopup] = useState(false);
  const [showLevelsRatesPopup, setShowLevelsRatesPopup] = useState(false);
  const [showJobTitlesPopup, setShowJobTitlesPopup] = useState(false);
  const [showManageMomentUserPopup, setShowManageMomentUserPopup] = useState(false);
  const [showEmailRouting, setShowEmailRouting] = useState(false);
  const [showPersonalLinks, setShowPersonalLinks] = useState(false);
  const [showKFInvoiceExport, setShowKFInvoiceExport] = useState(false);
  // TSD VM Integration
  const [vmProjectNumber, setVmProjectNumber] = useState('');
  const [vmSending, setVmSending] = useState(false);
  // API calls are handled via Redux thunks

  const { invoiceNum, showSuccess, errorInvNum } = useSelector((state) => state.invoice);
  const { configuration, showSettingsSuccess, emailRoutingRules, isEmailRoutingLoading } = useSelector((state) => state.settings);
  const { exportStatus } = useSelector((state) => state.suppliers);
  const { employeesExportStatus } = useSelector((state) => state.settings);

  // Access the enqueueSnackbar function from notistack
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(getConfiguration());
        dispatch(getCurrentInvNum());

        const p1 = dispatch(fetchAdminRequestedLeaveList({ approversEmail: account.username }));
        const p2 = dispatch(fetchAdminCalendarData());
        const p3 = dispatch(fetchAdminHolidayOverviewRows());
        await Promise.all([p1.unwrap(), p2.unwrap(), p3.unwrap()]);
        setIsDataFetched(true); // Mark that data has been successfully fetched

      } catch (err) {
        console.error("Error fetching data:", err);
        enqueueSnackbar('Failed to fetch data', { variant: 'error' });
      }
    };

    fetchData();
    // Keep this available if email routing gets its own endpoint; primary source will be configuration
    dispatch(getEmailRouting());
  }, [reload, account.username, dispatch, enqueueSnackbar]); // Add enqueueSnackbar as a dependency

  const location = useLocation();

  const handleInvoiceSubmit = async (invoiceNumber) => {
    try {
      if (InvoiceMode === 'ADJ') {
        console.log("Submitted Invoice Number:", invoiceNumber);
        await dispatch(postInvoiceNum(invoiceNumber)); // Dispatch action to update invoice number
      }
      else {
        // Handle the case when mode is 'UNPAID'
        await dispatch(postInvoiceNumUnpaid(invoiceNumber, account.username));
      }
    } catch (error) {
      // Show error message
      enqueueSnackbar('Failed to update invoice number', { variant: 'error' });
    }

    setShowInvoicePopup(false); // Close the popup after submission
  };

  const handleProjectNumSubmit = async (projectNumber) => {
    try {
      console.log("Submitted Project Number:", projectNumber);
      await dispatch(postResubmitFBP(projectNumber)); // Dispatch action to update invoice number
    } catch (error) {
      // Show error message
      enqueueSnackbar('Failed to update project number', { variant: 'error' });
    }

    setShowInvoicePopup(false); // Close the popup after submission
  };

  const handleResponsbilityFormSubmit = (formData) => {
    console.log("Form Data Submitted:", formData);

    const modifiedFormData = {
      ...formData,
      completedBy: account.username, // Add 'completedBy' key with value 'account'
    };

    dispatch(updateSTDTemplateResponsibilities(modifiedFormData));
  };


  const closeResponsiblityForm = () => {
    setShowFormResPopup(false);
  };

  useEffect(() => {
    if (showSuccess) {
      enqueueSnackbar('Invoice updated!', { variant: "success" });
      // Reset immediately after showing notification
      dispatch(setShowSuccess(false));
    }

    if (showSettingsSuccess) {
      enqueueSnackbar('Settings updated!', { variant: "success" });
      // Reset immediately after showing notification
      dispatch(setShowSettingsSuccess(false));
    }
  }, [showSuccess, showSettingsSuccess, dispatch]);

  const handleExportSuppliers = async () => {
    setSupplierLoading(true);
    await dispatch(exportSuppliers());
    setSupplierLoading(false);
  };

  const handleOnboardEmployee = async (formData) => {
    console.log('Attempting to onboard employee with data:', formData);
    try {
      const result = await dispatch(onboardEmployee({ formData })).unwrap();
      if (result?.success) {
        enqueueSnackbar(result.message || 'Employee onboarded successfully!', { variant: "success" });
        setShowOnboardPopup(false);
      } else {
        enqueueSnackbar(result?.message || 'Failed to onboard employee.', { variant: "error" });
      }
    } catch (error) {
      console.error("Error onboarding employee:", error);
      enqueueSnackbar('An unexpected error occurred while onboarding. Please try again.', { variant: "error" });
      // setShowOnboardPopup(false); // Decide if popup should close on catch
    }
  };


  // Dedicated logbook popup handles the API call itself

  return (
    <>
      {location.pathname === "/Admin/Settings" && (
        isDataFetched ? (
          <div className={styles.mainContent}>

            <div className={styles.content}>
              <div className={styles.title}>Excel/CSV Downloads</div>
            </div>
            <div className={styles.content}>
              <div className={styles.LeaveAdjustments}>
                <div
                  className={styles.textbox}
                  onClick={() => setShowLogbookPopup(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: showLogbookPopup ? 'not-allowed' : 'pointer', opacity: showLogbookPopup ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {showLogbookPopup ? 'Exporting Logbook...' : 'Download Logbook'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setWipLoading(true);
                    try {
                      // Dispatch all three export actions
                      await Promise.all([
                        dispatch(exportTimesheets()),
                        dispatch(fetchInvoiceExport()),
                        dispatch(fetchPhasesExport())
                      ]);
                    } catch (error) {
                      console.error("Error during multi-export:", error);
                      enqueueSnackbar('Error exporting data. Please try again.', { variant: 'error' });
                    } finally {
                      setWipLoading(false);
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: wipLoading ? 'not-allowed' : 'pointer', opacity: wipLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {wipLoading ? 'Exporting WIP...' : 'WIP (Work In Progress)'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setBillingLoading(true);
                    await dispatch(fetchInvoiceExport());
                    setBillingLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: billingLoading ? 'not-allowed' : 'pointer', opacity: billingLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {billingLoading ? 'Exporting Billing...' : 'Billing Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setClientLoading(true);
                    await dispatch(fetchClientExport());
                    setClientLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: clientLoading ? 'not-allowed' : 'pointer', opacity: clientLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {clientLoading ? 'Exporting Clients...' : 'Client Data'}
                </div>

              </div>

              <div className={styles.LeaveAdjustments}>
                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setKfInvoiceLoading(true);
                    await dispatch(fetchKFInvoiceExport());
                    setKfInvoiceLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: kfInvoiceLoading ? 'not-allowed' : 'pointer', opacity: kfInvoiceLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {kfInvoiceLoading ? 'Exporting KashFlow Invoice...' : 'KashFlow Invoice Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setKfPaidLoading(true);
                    await dispatch(fetchKFPaidExport());
                    setKfPaidLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: kfPaidLoading ? 'not-allowed' : 'pointer', opacity: kfPaidLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {kfPaidLoading ? 'Exporting KashFlow Paid...' : 'KashFlow Paid Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={() => setShowKFInvoiceExport(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  Export Individual KF Invoice
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setTimesheetLoading(true);
                    await dispatch(exportTimesheets());
                    setTimesheetLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: timesheetLoading ? 'not-allowed' : 'pointer', opacity: timesheetLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {timesheetLoading ? 'Exporting Timesheets...' : 'Timesheet Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    if (employeesLoading || employeesExportStatus === 'loading') return;
                    setEmployeesLoading(true);
                    await dispatch(exportEmployees());
                    setEmployeesLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: employeesLoading || employeesExportStatus === 'loading' ? 'not-allowed' : 'pointer', opacity: employeesLoading || employeesExportStatus === 'loading' ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {employeesLoading || employeesExportStatus === 'loading' ? 'Exporting Employees...' : 'Employees Data'}
                </div>
              </div>

              <div className={styles.LeaveAdjustments}>
                <div
                  className={styles.textbox}
                  onClick={!supplierLoading ? handleExportSuppliers : null}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: supplierLoading || exportStatus === 'loading' ? 'not-allowed' : 'pointer', opacity: supplierLoading || exportStatus === 'loading' ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {supplierLoading || exportStatus === 'loading' ? 'Exporting Suppliers...' : 'Suppliers Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setProjectLoading(true);
                    await dispatch(fetchFullProjectExport());
                    setProjectLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: projectLoading ? 'not-allowed' : 'pointer', opacity: projectLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {projectLoading ? 'Exporting Projects...' : 'Full Project Data'}
                </div>

                <div
                  className={styles.textbox}
                  onClick={async () => {
                    setPhaseLoading(true);
                    await dispatch(fetchPhasesExport());
                    setPhaseLoading(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: phaseLoading ? 'not-allowed' : 'pointer', opacity: phaseLoading ? 0.6 : 1 }}
                >
                  <FileDownloadIcon sx={{ fontSize: '16px' }} />
                  {phaseLoading ? 'Exporting Phases...' : 'Phase Data'}
                </div>
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.title}>Project & Invoice</div>
            </div>
            <div className={styles.content}>
              <div className={styles.LeaveAdjustments}>
                <div className={styles.textbox} onClick={() => { setShowFBPResub(true) }}>Resubmit FBP</div>
                <div className={styles.textbox} onClick={() => { setShowInvoicePopup(true); setInvoiceMode('ADJ') }}> Invoice Number Adjustment</div>
                <div className={styles.textbox} onClick={() => { setShowInvoicePopup(true); setInvoiceMode('UNPAID') }}> Mark Invoice as Unpaid</div>
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.title}>Company Settings</div>
            </div>
            <div className={styles.content}>
              <div className={styles.LeaveAdjustments}>
                <div className={styles.textbox} onClick={() => setShowPersonalLinks(true)}> Personal Dashboard Links</div>
                <div className={styles.textbox} onClick={() => setShowFormResPopup(true)}> Standards/Templates</div>
                <div className={styles.textbox} onClick={() => setShowEmailRouting(true)}> Configure Email Routing</div>
              </div>
            </div>

            {/* TSD Azure VM Integration */}
            <div className={styles.content}>
              <div className={styles.title}>TSD Azure VM</div>
            </div>
            <div className={styles.content}>
              <div className={styles.vmContainer}>
                <div className={styles.vmInputWrapper}>
                  <Textfeild
                    placeholder="Enter Project Number"
                    initialData={vmProjectNumber}
                    onValueChange={(val) => setVmProjectNumber(val)}
                  />
                </div>
                <div
                  className={styles.vmButton}
                  onClick={async () => {
                    if (vmSending || !vmProjectNumber.trim()) return;
                    setVmSending(true);
                    try {
                      // Pass userEmail (account.username) as second argument
                      const result = await dispatch(sendProjectToVm(vmProjectNumber.trim(), account?.username));
                      enqueueSnackbar("Your TSD is being created. We'll send you an email as soon as it's ready!", { variant: 'success' });
                      setVmProjectNumber('');
                    } catch (err) {
                      enqueueSnackbar('Failed to create TSD. Please try again or create an IT ticket.', { variant: 'error' });
                    } finally {
                      setVmSending(false);
                    }
                  }}
                  style={{
                    cursor: vmSending ? 'not-allowed' : 'pointer',
                    opacity: vmSending ? 0.6 : 1
                  }}
                >
                  {vmSending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <span>Create TSD</span>
                  )}
                </div>
              </div>
            </div>

            {(wipLoading || billingLoading || clientLoading || supplierLoading || kfInvoiceLoading || kfPaidLoading || timesheetLoading || projectLoading || phaseLoading) ? (
              <div className={styles.ProfileHolidayOverlay}>
                <CircularProgress size={65} />
              </div>
            ) : (
              isResponseShown && (
                <div className={styles.ProfileHolidayOverlay}>
                  <div className={styles.ProfileHolidayResponseBox}>
                    <div
                      style={{
                        fontSize: 16,
                        fontFamily: 'PT Sans',
                        textAlign: 'center',
                        margin: '20px',
                        marginLeft: '25px',
                        marginRight: '25px'
                      }}
                      dangerouslySetInnerHTML={{ __html: apiResponse }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <button
                        className={styles.ProfileHolidayButton}
                        onClick={() => setIsResponseShown(false)}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )
            )}

            <InvoiceNumPopup
              data={invoiceNum}
              isVisible={showInvoicePopup}
              onClose={() => setShowInvoicePopup(false)}
              onSubmit={handleInvoiceSubmit}
              mode={InvoiceMode}
            />

            <ProjectNumPopUp
              isVisible={showFBPResub}
              onClose={() => setShowFBPResub(false)}
              onSubmit={handleProjectNumSubmit}
            />

            {showFormResPopup && (
              <ResponsibilitiesForm
                onSubmit={handleResponsbilityFormSubmit}
                onClose={closeResponsiblityForm}
              />
            )}

            {/* Employee Management Section */}
            <div className={styles.content}>
              <div className={styles.title}>Employee Management</div>
            </div>
            <div className={styles.content}>
              <div className={styles.LeaveAdjustments}>
                <div
                  className={styles.textbox}
                  onClick={() => setShowOnboardPopup(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Onboard New Employee
                </div>
                <div
                  className={styles.textbox}
                  onClick={() => setShowManageMomentUserPopup(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Manage Moment User
                </div>
                <div
                  className={styles.textbox}
                  onClick={() => setShowLevelsRatesPopup(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Manage Levels & Rates
                </div>
                <div
                  className={styles.textbox}
                  onClick={() => setShowJobTitlesPopup(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Manage Job Titles
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        )
      )}
      {showInvoicePopup && <InvoiceNumPopup onClose={() => setShowInvoicePopup(false)} defaultSelectedMode={InvoiceMode} onSubmit={handleInvoiceSubmit} />}
      {showFBPResub && <ProjectNumPopUp onClose={() => setShowFBPResub(false)} onSubmit={handleProjectNumSubmit} />}
      {showLogbookPopup && <ProjectLogbookPopUp isVisible={showLogbookPopup} onClose={() => setShowLogbookPopup(false)} />}
      {showFormResPopup && <ResponsibilitiesForm onSubmit={handleResponsbilityFormSubmit} onClose={closeResponsiblityForm} />}
      {showEmailRouting && (
        <EmailRoutingForm
          initialRules={(function cfgRules() {
            // Prefer rules delivered via configuration API; fall back to dedicated store if absent
            const cfg = configuration || {};
            // Try common keys
            const candidate = cfg.emailRouting || cfg.email_routing || cfg.notification_rules || cfg.notifications || (cfg.settings && (cfg.settings.emailRouting || cfg.settings.notifications));
            if (Array.isArray(candidate)) return candidate;
            // If server nests rules under { rules: [...] }
            if (candidate && Array.isArray(candidate.rules)) return candidate.rules;
            return emailRoutingRules || [];
          })()}
          onSubmit={(rules) => { dispatch(updateEmailRouting(rules)); }}
          onClose={() => setShowEmailRouting(false)}
        />
      )}
      {showPersonalLinks && (
        <PersonalLinksForm onClose={() => setShowPersonalLinks(false)} />
      )}
      {showOnboardPopup && <OnboardEmployeePopup onSubmit={handleOnboardEmployee} onClose={() => setShowOnboardPopup(false)} />}
      {showLevelsRatesPopup && (
        <LevelsRatesPopup onClose={() => setShowLevelsRatesPopup(false)} />
      )}
      {showJobTitlesPopup && (
        <JobTitlesPopup onClose={() => setShowJobTitlesPopup(false)} />
      )}
      {showManageMomentUserPopup && (
        <ManageMomentUserPopup onClose={() => setShowManageMomentUserPopup(false)} />
      )}
      {showKFInvoiceExport && (
        <KFInvoiceExportPopup
          isVisible={showKFInvoiceExport}
          onClose={() => setShowKFInvoiceExport(false)}
        />
      )}

      <Outlet />
    </>
  );
}

export default Settings;
